import { Form } from 'antd';
import cn from 'classnames';
import { cloneDeep, isNumber } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { FC } from 'react';
import type { InputNumberProps } from 'antd/lib/input-number';

import Modal from '@/shared/Modal';
import Button from '@/shared/Button';
import { InputNumber } from '@/shared/MiInputNumber/MiInputNumber';
import type { RootDispatch } from '@/components/WellLoggingOperations/types';
import type { CurvesState, ResearchesByDate } from '@/components/WellLoggingOperations/types';
import type { Curve, Research, ResearchesState, RootState } from '@/components/WellLoggingOperations/types';

// Эти магические числа используются для создания различных идентификаторов в масштабах - копиях, копиях ресерчей и копиях curve.
// С помощью этих магических чисел мы в разных местах проекта можем идентифицировать, является ли ресёрч или curve копией или оригиналом.
export const KEY_COPY_STRING = '-0-0-0-0-0-';
export const CODE_COPY_STRING = '000000';
export const CURVE_COPY_NUMBER = 100000;
export const DEFAULT_STEP = 10;

type InputProps = InputNumberProps;

const Input: FC<InputProps> = props => {
    const { title, min, max, step, value, onBlur, onChange, disabled } = props;

    return (
        <div className="flex flex-col">
            <span
                style={{ marginBottom: 8, lineHeight: 1, fontSize: 11 }}
                className={cn('font-medium', { 'text-gray-800': disabled })}
            >
                {title}
            </span>
            <InputNumber
                disabled={disabled}
                value={value}
                step={step}
                onChange={onChange}
                onBlur={onBlur}
                min={min}
                max={max}
                className="w-full"
            />
        </div>
    );
};

export type ScalesType = 'min' | 'max' | 'step';

type ScaleProps = {
    onChange: (index: number) => (type: ScalesType) => (value: string | number | null | undefined) => void;
    index: number;
    disabled: boolean;
    curve: Curve | null;
};

type FieldType = {
    'min-0': number;
    'step-0': number;
    'max-0': number;
    'min-1': number;
    'step-1': number;
    'max-1': number;
    'min-2': number;
    'step-2': number;
    'max-2': number;
};

const Scale: FC<ScaleProps> = props => {
    const { onChange, index, disabled, curve } = props;

    const maxStepValue =
        curve && isNumber(curve?.xStop) && isNumber(curve?.xStart) ? (curve?.xStop - curve?.xStart) / 2 : Infinity;

    return (
        <div className="flex flex-row items-center justify-start gap-4">
            <div className={cn('w-20 text-xs font-medium', { 'text-gray-800': disabled })}>{index + 1} масштаб</div>
            <Form.Item<FieldType>
                name={`min-${index}` as keyof FieldType}
                className={'scales_custom-form-item'}
                noStyle
                rules={
                    !disabled
                        ? [
                              { required: true, message: 'Поле не может быть пустым' },
                              () => ({
                                  validator(_, value) {
                                      if (value.length > 10) {
                                          return Promise.reject('Значение не может быть больше 999999');
                                      }
                                      if (curve && value >= curve?.xStop) {
                                          return Promise.reject(
                                              'Минимальное значение не может превышать или быть равно максимальному',
                                          );
                                      }
                                      return Promise.resolve();
                                  },
                              }),
                          ]
                        : []
                }
            >
                <Input
                    disabled={disabled}
                    onChange={onChange(index)('min')}
                    step={0.1}
                    title={'Минимальное значение'}
                />
            </Form.Item>
            <Form.Item<FieldType>
                name={`step-${index}` as keyof FieldType}
                className={'scales_custom-form-item'}
                noStyle
                rules={
                    !disabled
                        ? [
                              { required: true, message: 'Поле не может быть пустым' },
                              () => ({
                                  validator(_, value) {
                                      if (value > maxStepValue) {
                                          return Promise.reject(
                                              'Значение шага не может превышать половину от разницы max и min',
                                          );
                                      }
                                      if (value < 0.1) {
                                          return Promise.reject('Значение шага не может быть меньше 0.1');
                                      }
                                      return Promise.resolve();
                                  },
                              }),
                          ]
                        : []
                }
            >
                <Input disabled={disabled} onChange={onChange(index)('step')} step={0.1} title={'Шаг сетки'} />
            </Form.Item>
            <Form.Item<FieldType>
                name={`max-${index}` as keyof FieldType}
                className={'scales_custom-form-item'}
                noStyle
                rules={
                    !disabled
                        ? [
                              { required: true, message: 'Поле не может быть пустым' },
                              () => ({
                                  validator(_, value) {
                                      if (value.length > 8) {
                                          return Promise.reject('Значение не может быть больше 999999');
                                      }
                                      if (curve && value < curve?.xStart) {
                                          return Promise.reject(
                                              'Максимальное значение не может быть меньше минимального',
                                          );
                                      }
                                      return Promise.resolve();
                                  },
                              }),
                          ]
                        : []
                }
            >
                <Input
                    disabled={disabled}
                    onChange={onChange(index)('max')}
                    step={0.1}
                    title={'Максимальное значение'}
                />
            </Form.Item>
        </div>
    );
};

export type Scale = {
    xStart: number;
    xStop: number;
    xStep?: number;
    parentCurve?: number;
} & Research;

type ScaleSettingsModalProps = {
    visible: boolean;
    onCancel: () => void;
    data: Research;
    wellId: number;
};

export type LocalResearches = Record<string, ResearchesByDate[]>;
export type LocalCurves = Record<string, Curve>;

const defaultFormValues = {
    'min-0': null,
    'step-0': null,
    'max-0': null,
    'min-1': null,
    'step-1': null,
    'max-1': null,
    'min-2': null,
    'step-2': null,
    'max-2': null,
};

export const ScaleSettingsModal: FC<ScaleSettingsModalProps> = ({ visible, onCancel, data, wellId }) => {
    const dispatch = useDispatch<RootDispatch>();
    const { data: curvesData } = useSelector<RootState, CurvesState>(state => state.curves);
    const { data: researchesData } = useSelector<RootState, ResearchesState>(state => state.researches);

    const [scales, setScales] = useState<Array<Scale | null>>([]);
    const [localResearches, setLocalResearches] = useState<LocalResearches>(cloneDeep(researchesData));
    const [localCurves, setLocalCurves] = useState<LocalCurves>(cloneDeep(curvesData));

    const [form] = Form.useForm();

    const path = useMemo(
        () =>
            data?.key
                .replace('research-', '')
                .split('-')
                .map((c: string) => Number(c) - 1),
        [data?.key],
    );

    const oldFieldValues = form.getFieldsValue();

    useEffect(() => {
        const newValues = { ...oldFieldValues };

        scales.forEach((scale, index) => {
            const curve = scale?.curve && localCurves[scale?.curve] ? localCurves[scale?.curve] : null;

            newValues[`min-${index}`] = curve?.xStart.toFixed(1);
            newValues[`max-${index}`] = curve?.xStop.toFixed(1);
            newValues[`step-${index}`] = curve ? (curve.xStep ? curve?.xStep : DEFAULT_STEP)?.toFixed(1) : undefined;
        });

        form.setFieldsValue(newValues);
    }, [localCurves, oldFieldValues, scales]);

    useEffect(() => {
        if (visible) {
            setLocalResearches(researchesData);
        }
    }, [researchesData, visible]);

    useEffect(() => {
        if (visible) {
            getAllRelatedScales(localResearches);
        }
    }, [localResearches, visible]);

    const getAllRelatedScales = (localResearches: LocalResearches) => {
        const res =
            localResearches[wellId]?.[path[0]].children[path[1]]?.children.filter(c => c.key.includes(data.key)) ?? [];

        updateScales(res);
    };

    const updateScales = useCallback(scalesToAdd => {
        const newScales = [null, null, null];

        newScales.unshift(...scalesToAdd);
        newScales.length = 3;

        setScales(newScales);
    }, []);

    const currentNumberOfScales = useMemo(() => scales.filter(s => s?.key).length, [scales]);

    const makeDefaultValue = useCallback(
        (index: number): Scale | undefined =>
            scales[0]
                ? {
                      ...scales[0],
                      key: `${scales[0].key}${KEY_COPY_STRING}${index}`,
                      code: `${scales[0].code}${CODE_COPY_STRING}${index}`,
                      curve: scales[0].curve * CURVE_COPY_NUMBER + index,
                      title: `${scales[0]?.title} | ${index}`,
                  }
                : undefined,
        [scales],
    );

    const onNumberOfScalesChange = (value: number | undefined | null | string) => {
        if (!isNumber(value)) return;

        if (value > currentNumberOfScales) {
            const scalesToAdd: Scale[] = [];

            scales.forEach((s, i) => {
                if (!s?.key && i !== 0 && i <= value - 1) {
                    const newScale = makeDefaultValue(i);
                    if (newScale) {
                        scalesToAdd.push(newScale);
                    } else {
                        throw new Error('Непредвиденная ошибка');
                    }
                }
            });

            add(scalesToAdd);
        } else {
            const scalesToRemove: Scale[] = [];

            scales.forEach((s, i) => {
                if (i !== 0 && i > value - 1) {
                    if (s) {
                        scalesToRemove.push(s);
                    }
                }
            });

            remove(scalesToRemove);
        }
    };

    const onChange = useCallback(
        (index: number) => (type: ScalesType) => (value: number | string | null | undefined) => {
            const curve = scales[index];

            if (isNumber(value)) {
                const newCurvesData = Object.fromEntries(
                    Object.entries(localCurves).map(([id, item]) => {
                        if (item.id === curve?.curve) {
                            if (type === 'min') {
                                item.xStart = value;
                            } else if (type === 'max') {
                                item.xStop = value;
                            } else if (type === 'step') {
                                item.xStep = value;
                            }
                        }

                        return [id, item];
                    }),
                );

                setLocalCurves(newCurvesData);
            }
        },
        [localCurves, scales],
    );

    const add = (items: Scale[]) => {
        const initialScale = scales[0];
        const lastScale = [...scales].reverse().find(s => s);

        if (lastScale?.curve && initialScale?.curve) {
            const lastCurve = localCurves[lastScale.curve];
            const researches = cloneDeep(localResearches);
            const curves = cloneDeep(localCurves);

            items.forEach(scale => {
                const children = [...researches[wellId][path[0]].children[path[1]].children];
                // findLastIndex - full support в edge 97
                // @ts-ignore
                const indexOfParent = children.findLastIndex(oc => scale.key.includes(oc.key));
                children.splice(children.length - indexOfParent, 0, scale);

                // Mutate by reference!
                researches[wellId][path[0]].children[path[1]].children = children;

                curves[scale.curve] = {
                    ...lastCurve,
                    id: scale.curve,
                };
            });

            setLocalCurves(curves);
            setLocalResearches(researches);
        } else {
            throw new Error('Непредвиденная ошибка');
        }
    };

    const remove = (items: Scale[]) => {
        const researches = cloneDeep(localResearches);
        const curves = cloneDeep(localCurves);

        items.forEach(scale => {
            const newChildren = researches[wellId][path[0]].children[path[1]].children.filter(c => c.key !== scale.key);

            researches[wellId][path[0]].children[path[1]].children = newChildren;

            delete curves[scale.curve];
        });

        setLocalCurves(curves);
        setLocalResearches(researches);
    };

    const onOk = () => form.submit();

    const onFinish = () => {
        dispatch.curves.setNewCurves(localCurves);
        dispatch.researches.setResearches({ data: localResearches[wellId], wellId: String(wellId) });
        onCancel();
    };

    const onFinishFailed = (errors: any) => {
        alert(
            errors.errorFields
                .map((c: any) => c.errors)
                .flat()
                .join('\n \n'),
        );
    };

    return (
        <Modal
            className="scales_modal"
            title="Изменение шкалы"
            onCancel={onCancel}
            width={612}
            destroyOnHidden
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Отмена
                </Button>,
                <Button key="submit" htmlType="submit" type="primary" onClick={onOk}>
                    Ок
                </Button>,
            ]}
            open={visible}
        >
            <div className="flex flex-col px-4 pb-4 pt-6">
                <div className="mb-5 flex max-w-[9rem] flex-col">
                    <Input
                        value={currentNumberOfScales}
                        onChange={onNumberOfScalesChange}
                        step={1}
                        min={1}
                        max={3}
                        title={'Количество масштабов'}
                    />
                </div>
                <div className="flex flex-col">
                    <Form
                        name="scales-form"
                        form={form}
                        validateTrigger={'onSubmit'}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        initialValues={defaultFormValues}
                        autoComplete="off"
                    >
                        <div className="flex flex-col gap-5">
                            {scales.map((scale, index) => (
                                <Scale
                                    key={index}
                                    curve={scale?.curve && localCurves[scale?.curve] ? localCurves[scale?.curve] : null}
                                    disabled={!scale?.key}
                                    onChange={onChange}
                                    index={index}
                                />
                            ))}
                        </div>
                    </Form>
                </div>
            </div>
        </Modal>
    );
};
